/*
 * Copyright 2025-2026, Airspan
 * All Rights Reserved.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Tooltip } from '@mui/material';

interface AccountMenuItem {
    label: string;
    icon: string;
    onClick: () => void;
    separatorAfter?: boolean;
}

interface AccountMenuProps {
    username: string;
    items: AccountMenuItem[];
}

const AccountMenu: React.FC<AccountMenuProps> = ({ username, items }) => {
    const [open, setOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const initials = username
        .split(/[\s._@]+/)
        .filter(Boolean)
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase() || 'U';

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open]);

    return (
        <div ref={menuRef} style={{ position: 'relative', display: 'inline-flex' }}>
            <Tooltip title="Account settings">
                <button
                    type="button"
                    aria-label="Account settings"
                    onClick={() => setOpen((v) => !v)}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        border: '1px solid rgba(255,255,255,0.3)',
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'var(--primary-foreground)',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        padding: 0,
                        transition: 'background-color 150ms ease',
                    }}
                    onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255,255,255,0.15)';
                    }}
                    onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255,255,255,0.2)';
                    }}
                >
                    {initials}
                </button>
            </Tooltip>

            {open && (
                <div
                    style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: 4,
                        width: 224,
                        backgroundColor: 'var(--color-background)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'calc(var(--radius) - 2px)',
                        boxShadow: 'var(--shadow-card)',
                        zIndex: 1200,
                        overflow: 'hidden',
                        padding: 4,
                    }}
                >
                    {/* User info header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px' }}>
                        <span className="material-icons" style={{ fontSize: 16, color: 'var(--color-foreground)' }}>
                            person
                        </span>
                        <div style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
                            <div
                                style={{
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: 'var(--color-foreground)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {username}
                            </div>
                        </div>
                    </div>

                    {/* Separator */}
                    <div style={{ height: 1, margin: '4px -4px', backgroundColor: 'var(--color-muted)' }} />

                    {/* Menu items */}
                    {items.map((item, index) => (
                        <React.Fragment key={item.label}>
                            <div
                                role="menuitem"
                                tabIndex={0}
                                onClick={() => {
                                    item.onClick();
                                    setOpen(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        item.onClick();
                                        setOpen(false);
                                    }
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '6px 8px',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    fontWeight: 400,
                                    borderRadius: 'calc(var(--radius) - 4px)',
                                    color: 'var(--color-foreground)',
                                    backgroundColor: 'transparent',
                                    transition: 'background-color 150ms ease',
                                }}
                                onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLDivElement).style.backgroundColor = 'var(--color-accent)';
                                }}
                                onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
                                }}
                            >
                                <span className="material-icons" style={{ fontSize: 16 }}>
                                    {item.icon}
                                </span>
                                {item.label}
                            </div>
                            {item.separatorAfter && index < items.length - 1 && (
                                <div style={{ height: 1, margin: '4px -4px', backgroundColor: 'var(--color-muted)' }} />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AccountMenu;
